// Square mono buttons and actions (RESTYLE §2).
//   <Button variant? size? icon? arrow? keys? truncate? {...button attrs} />
//       variant: primary (filled accent: the main action of a view that is not the gold CTA) · cta (the gold square, once per view)
//                · danger (red outline; Cancel run, Remove) · secondary (ink outline, the default) · ghost (text, underline on hover)
//       size: sm | md | lg · icon: an IconName before the label · truncate: the label ellipsizes (long file names on phones)
//   buttonClass({ variant?, size?, className? })   the same classes for an <a> or router <Link>
//   <ArmedButton label confirmLabel? seconds? onConfirm variant? size? icon? disabled? />
//       two-step destructive: the first click arms ("Confirm cancel · 4 s"), the second confirms; reverts after `seconds`; Esc disarms.
//       variant defaults to danger: red outline, filled red while armed. confirmLabel defaults to "Confirm <first word of label>".
//   <ActionLink {...anchor attrs}>Open printable report</ActionLink> · <ActionButton {...button attrs}>…</ActionButton>   mono action + arrow
import { useEffect, useState, type AnchorHTMLAttributes, type ButtonHTMLAttributes } from "react";
import { Arrow } from "./Glyphs";
import { Icon, type IconName } from "./Icon";
import { cx } from "./cx";

type ButtonVariant = "primary" | "cta" | "danger" | "secondary" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

export function buttonClass({ variant = "secondary", size = "md", className }: { variant?: ButtonVariant; size?: ButtonSize; className?: string } = {}): string {
  return cx("btn", `btn--${variant}`, size !== "md" && `btn--${size}`, className);
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** a 14px hairline icon before the label */
  icon?: IconName;
  /** trailing arrow (moves 3px on hover) */
  arrow?: boolean;
  /** key hint rendered as <kbd>s after the label, e.g. ["Ctrl", "Enter"] */
  keys?: string[];
  /** long labels (file names) ellipsize instead of overflowing a narrow column */
  truncate?: boolean;
};

export function Button({ variant, size, icon, arrow, keys, truncate, className, children, type = "button", ...rest }: ButtonProps) {
  return (
    <button type={type} className={buttonClass({ variant, size, className: cx(truncate && "max-w-full", className) })} {...rest}>
      {icon && <Icon name={icon} size={14} />}
      {truncate ? <span className="min-w-0 truncate">{children}</span> : children}
      {arrow && <Arrow />}
      {keys && (
        <span className="inline-flex gap-1">
          {keys.map((k) => (
            <kbd key={k}>{k}</kbd>
          ))}
        </span>
      )}
    </button>
  );
}

export function ArmedButton({
  label,
  confirmLabel,
  seconds = 4,
  onConfirm,
  variant = "danger",
  size,
  icon,
  disabled,
  className,
}: {
  label: string;
  confirmLabel?: string;
  seconds?: number;
  onConfirm: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  disabled?: boolean;
  className?: string;
}) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return;
    const t = window.setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => window.clearTimeout(t);
  }, [left]);
  const armed = left > 0;
  return (
    <>
      <button
        type="button"
        className={buttonClass({ variant, size, className })}
        data-armed={armed ? "true" : undefined}
        disabled={disabled}
        onClick={() => {
          if (armed) {
            setLeft(0);
            onConfirm();
          } else setLeft(seconds);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape" && armed) {
            e.stopPropagation();
            setLeft(0);
          }
        }}
      >
        {icon && <Icon name={icon} size={14} />}
        {armed ? (
          <>
            {confirmLabel ?? `Confirm ${label.split(" ")[0].toLowerCase()}`}{" "}
            <span className="count">· {left}&nbsp;s</span>
          </>
        ) : (
          label
        )}
      </button>
      <span className="sr-only" role="status">
        {armed ? `Press again within ${seconds} seconds to confirm.` : ""}
      </span>
    </>
  );
}

export function ActionLink({ className, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a className={cx("action", className)} {...rest}>
      {children}
      <Arrow />
    </a>
  );
}

export function ActionButton({ className, children, type = "button", ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type={type} className={cx("action", className)} {...rest}>
      {children}
      <Arrow />
    </button>
  );
}
